/**
 * StaticMapImage — Google Static Maps background for the report Site-Overview
 * map on the EXPORT/PDF path (J7).
 *
 * The in-app viewer keeps the live `<Map>` (real Google tiles). But the live
 * `<Map mapId=…>` is a VECTOR (WebGL) map: in the headless print Chromium tiles
 * never paint, so the exported overview shipped as a blank frame. This renders
 * a STATIC Google Map PNG (boundary + KMZ layers baked in via Static-Maps
 * overlays, so Google owns the projection) as the export base layer instead.
 *
 * Two credential paths to the `/api/static-map` route (the Maps key stays
 * server-side either way):
 *   - HEADLESS /print tab: it carries the signed print HMAC token in the page
 *     URL (`?token=…`) but has no Firebase session and an <img> can't send an
 *     Authorization header — so we put the same token on the image URL.
 *   - In-app EXPORT PREVIEW (window.open('/print/{id}') with a live session):
 *     no token → fetch the PNG via `authedFetch` (Firebase bearer) into a blob
 *     URL and point the <img> at that.
 *
 * Honest degradation: no center, no Maps key (503), or any fetch/render error
 * ⇒ render `fallback` (the pure-SVG boundary diagram) — never a blank/broken
 * map. The component owns the success/error switch so the caller just passes
 * the SVG as `fallback`.
 */
import React from 'react';
import { buildStaticMapParams, staticMapQuery, type BuildStaticMapArgs } from '../../../lib/siteStaticMap';
import { authedFetch } from '../../../lib/authedFetch';

export interface StaticMapImageProps extends BuildStaticMapArgs {
  /** Report id — matched against the print token's claim by the server. */
  reportId?: string;
  /** Rendered on any failure (no center / no key / fetch / decode error). */
  fallback: React.ReactNode;
  className?: string;
  alt?: string;
}

/** The signed print HMAC token from the headless /print tab URL, if present. */
function readPrintToken(): string {
  if (typeof window === 'undefined') return '';
  try {
    return new URLSearchParams(window.location.search).get('token') ?? '';
  } catch {
    return '';
  }
}

export const StaticMapImage: React.FC<StaticMapImageProps> = ({
  reportId,
  fallback,
  className,
  alt = 'Site location map',
  ...buildArgs
}) => {
  const params = React.useMemo(() => buildStaticMapParams(buildArgs), [
    // Recompute only when the geometry inputs change.
    buildArgs.center?.lat,
    buildArgs.center?.lng,
    buildArgs.boundary,
    buildArgs.extraRings,
    buildArgs.ringColors,
    buildArgs.bbox,
    buildArgs.layers,
    buildArgs.size?.w,
    buildArgs.size?.h,
    buildArgs.maptype,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const [failed, setFailed] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);

  const printToken = readPrintToken();

  // No usable params → straight to the SVG fallback (no network call).
  const query = params ? staticMapQuery(params, printToken ? { token: printToken, reportId: reportId ?? '' } : undefined) : null;
  // Headless /print: a token means we can use a bare <img src> (no Firebase
  // session, no bearer). In-app export-preview has no token → fetch a blob.
  const useTokenImg = Boolean(printToken) && Boolean(query);
  const directSrc = useTokenImg ? `/api/static-map?${query}` : null;

  React.useEffect(() => {
    // Blob path — only when there's no print token (in-app export preview with
    // a live Firebase session). Fetch the PNG with the bearer attached.
    if (!query || useTokenImg) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const resp = await authedFetch(`/api/static-map?${query}`);
        if (!resp.ok) throw new Error(`static-map ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [query, useTokenImg]);

  if (!query || failed) return <>{fallback}</>;

  const src = directSrc ?? blobUrl;
  if (!src) {
    // Blob still loading — show the SVG fallback underneath so there is never
    // a blank box mid-flight; it is replaced once the PNG resolves.
    return <>{fallback}</>;
  }

  return (
    <img
      data-testid="property-map-static-image"
      src={src}
      alt={alt}
      className={className ?? 'absolute inset-0 w-full h-full object-cover'}
      onError={() => setFailed(true)}
    />
  );
};
